import React, { useState, useEffect, useCallback } from 'react';

interface CostsData {
  mtd: number;
  projected: number;
  error: string | null;
}

function Costs() {
  const [costs, setCosts] = useState<CostsData>({ mtd: 0, projected: 0, error: null });
  const [loading, setLoading] = useState(true);

  const fetchCosts = useCallback(async () => {
    try {
      const response = await fetch('/api/costs');
      if (!response.ok) {
        if (response.status === 401) {
          window.location.href = '/login';
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setCosts(data);
    } catch (err) {
      setCosts({ mtd: 0, projected: 0, error: err instanceof Error ? err.message : 'Unknown error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCosts();
    const interval = setInterval(fetchCosts, 60000); // Poll every minute
    return () => clearInterval(interval);
  }, [fetchCosts]);

  // Budget set to $50/month for moltbot infrastructure
  const budget = 50.00;
  const percentUsed = costs.mtd > 0 ? (costs.mtd / budget) * 100 : 0;

  // Calculate daily average based on current day of month
  const today = new Date();
  const dayOfMonth = today.getDate();
  const dailyAverage = costs.mtd > 0 ? costs.mtd / dayOfMonth : 0;

  return (
    <div>
      <header className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold">Cost Tracking</h1>
          <p className="text-mc-muted mt-2">Monitor AWS infrastructure costs</p>
        </div>
        <button
          onClick={fetchCosts}
          className="text-mc-accent text-sm hover:underline"
          disabled={loading}
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </header>

      {costs.error && (
        <div className="mb-6 p-4 bg-red-900/20 border border-red-500 rounded-lg text-red-400">
          Error loading costs: {costs.error}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="card">
          <p className="text-mc-muted text-sm">Month to Date</p>
          {loading ? (
            <div className="animate-pulse bg-mc-border h-10 w-24 rounded mt-2"></div>
          ) : (
            <p className="text-4xl font-bold text-mc-accent mt-2">${costs.mtd.toFixed(2)}</p>
          )}
          <p className="text-sm text-mc-muted mt-2">Current spend</p>
        </div>
        <div className="card">
          <p className="text-mc-muted text-sm">Projected</p>
          {loading ? (
            <div className="animate-pulse bg-mc-border h-10 w-24 rounded mt-2"></div>
          ) : (
            <p className="text-4xl font-bold text-mc-warning mt-2">${costs.projected.toFixed(2)}</p>
          )}
          <p className="text-sm text-mc-muted mt-2">End of month estimate</p>
        </div>
        <div className="card">
          <p className="text-mc-muted text-sm">Budget</p>
          <p className="text-4xl font-bold text-mc-success mt-2">${budget.toFixed(2)}</p>
          <p className="text-sm text-mc-muted mt-2">{percentUsed.toFixed(0)}% used</p>
        </div>
        <div className="card">
          <p className="text-mc-muted text-sm">Daily Average</p>
          {loading ? (
            <div className="animate-pulse bg-mc-border h-10 w-24 rounded mt-2"></div>
          ) : (
            <p className="text-4xl font-bold text-mc-text mt-2">${dailyAverage.toFixed(2)}</p>
          )}
          <p className="text-sm text-mc-muted mt-2">Per day (this month)</p>
        </div>
      </div>

      {/* Budget Progress */}
      <div className="card">
        <h3 className="text-lg font-semibold mb-4">Budget Status</h3>
        <div className="relative pt-1">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-mc-text">
              ${costs.mtd.toFixed(2)} / ${budget.toFixed(2)}
            </span>
            <span className="text-sm font-semibold text-mc-success">
              ${Math.max(0, budget - costs.mtd).toFixed(2)} remaining
            </span>
          </div>
          <div className="w-full bg-mc-border rounded-full h-4">
            <div
              className={`rounded-full h-4 transition-all ${
                percentUsed > 80 ? 'bg-mc-danger' : percentUsed > 60 ? 'bg-mc-warning' : 'bg-mc-success'
              }`}
              style={{ width: `${Math.min(percentUsed, 100)}%` }}
            ></div>
          </div>
          <div className="flex justify-between mt-2 text-xs text-mc-muted">
            <span>$0</span>
            <span className="text-mc-warning">${(budget * 0.6).toFixed(0)} (60%)</span>
            <span className="text-mc-danger">${(budget * 0.8).toFixed(0)} (80%)</span>
            <span>${budget.toFixed(0)}</span>
          </div>
        </div>
      </div>

      {/* Info about detailed costs */}
      <div className="card mt-6">
        <h3 className="text-lg font-semibold mb-4">AWS Cost Details</h3>
        <p className="text-mc-muted text-sm">
          For detailed cost breakdown by service, visit the{' '}
          <a
            href="https://console.aws.amazon.com/cost-management/home"
            target="_blank"
            rel="noopener noreferrer"
            className="text-mc-accent hover:underline"
          >
            AWS Cost Explorer
          </a>
          . This dashboard shows aggregate costs for the Moltbot infrastructure.
        </p>
        <div className="mt-4 p-3 bg-mc-bg rounded-lg">
          <p className="text-sm">
            <span className="font-semibold">Expected services:</span> ECS Fargate, CloudWatch, EFS, SSM, Lambda
          </p>
        </div>
      </div>
    </div>
  );
}

export default Costs;
